import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { inviteMember, listMembers } from "../api/reports";
import { ApiError } from "../api/client";
import type { TenantMember } from "../types";
import { Modal, StatusPill, Avatar } from "../components/primitives";
import { PageHeader } from "../components/Chrome";
import { toast } from "../components/Toast";

export function AdminPeople({ tenantSlug }: { tenantSlug: string }) {
  const [members, setMembers] = useState<TenantMember[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);

  const reload = async () => {
    try {
      setMembers(await listMembers(tenantSlug));
    } catch (e) {
      setError(e instanceof ApiError ? e.detail : (e as Error).message);
    }
  };

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantSlug]);

  return (
    <div className="container">
      <PageHeader title="People">
        <button className="btn-primary" onClick={() => setInviteOpen(true)}>
          + Invite
        </button>
      </PageHeader>
      {error && <div className="empty err">{error}</div>}
      {members === null && !error && <div className="empty">Loading…</div>}
      {members && members.length === 0 && (
        <div className="empty">No members yet. Invite your first.</div>
      )}
      {members && members.length > 0 && (
        <table className="ppl-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {members.map((m) => (
              <tr key={m.id}>
                <td>
                  <div className="ppl-name">
                    <Avatar name={m.email} />
                    <span>{m.email.split("@")[0]}</span>
                  </div>
                </td>
                <td>{m.email}</td>
                <td>{m.role}</td>
                <td>
                  <StatusPill
                    status={
                      m.revoked_at
                        ? "revoked"
                        : m.accepted_at
                        ? "active"
                        : "invited"
                    }
                  />
                </td>
                <td>
                  <Link to={`/admin/people/${m.id}`} className="btn-link">
                    Manage access →
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {inviteOpen && (
        <InviteModal
          tenantSlug={tenantSlug}
          onClose={() => setInviteOpen(false)}
          onInvited={async () => {
            setInviteOpen(false);
            await reload();
          }}
        />
      )}
    </div>
  );
}

function InviteModal({
  tenantSlug,
  onClose,
  onInvited,
}: {
  tenantSlug: string;
  onClose: () => void;
  onInvited: () => Promise<void>;
}) {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    setSubmitting(true);
    setErr(null);
    try {
      await inviteMember(tenantSlug, { email, role: "viewer" });
      toast(`Invited ${email}`);
      await onInvited();
    } catch (e) {
      setErr(e instanceof ApiError ? e.detail : (e as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal onClose={onClose} width={420}>
      <h2>Invite a person</h2>
      <p style={{ color: "var(--ink-3)" }}>
        Sends them a Google sign-in link for {tenantSlug}.
      </p>
      <input
        type="email"
        placeholder="email@example.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="text-input"
        autoFocus
      />
      {err && <div className="form-err">{err}</div>}
      <div className="modal-actions">
        <button className="btn-secondary" onClick={onClose} disabled={submitting}>
          Cancel
        </button>
        <button
          className="btn-primary"
          onClick={submit}
          disabled={!email || submitting}
        >
          {submitting ? "Inviting…" : "Send invite"}
        </button>
      </div>
    </Modal>
  );
}
